/**
 * Epic 278 Phase 3 Slice A — site control-stage rollup from Case register rows.
 * Controlled vocabulary only (`CONTROL_STAGES` / mapControlStage). No invented stages.
 */

import { CONTROL_STAGES, type ControlStage } from './workspaceResolvedState';
import type { CaseRegisterRow } from './workspaceRegisters';

export type ControlStageRollupBucket = {
  stage: ControlStage;
  count: number;
};

export type ControlStageOperationalKey =
  | 'awaiting_implementation'
  | 'awaiting_remeasurement'
  | 'coverage_blocked'
  | 'new_activity';

export type ControlStageOperationalBucket = {
  key: ControlStageOperationalKey;
  label: string;
  count: number;
};

export type ControlStageRollup = {
  byStage: ControlStageRollupBucket[];
  operational: ControlStageOperationalBucket[];
  total: number;
};

const AWAITING_IMPLEMENTATION = new Set<ControlStage>([
  'Action assigned',
  'Implementation in progress',
  'Implementation verification required',
]);

const AWAITING_REMEASUREMENT = new Set<ControlStage>([
  'Follow-up measurement scheduled',
  'Measurement in progress',
]);

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function isCoverageBlocked(row: CaseRegisterRow): boolean {
  return normalize(row.latestScan) === 'insufficient coverage';
}

function hasNewActivity(row: CaseRegisterRow): boolean {
  const label = normalize(row.newActivity);
  return Boolean(label) && label !== 'no' && label !== '—';
}

export function buildControlStageRollup(rows: CaseRegisterRow[]): ControlStageRollup {
  const counts = new Map<ControlStage, number>();
  for (const stage of CONTROL_STAGES) counts.set(stage, 0);

  let awaitingImplementation = 0;
  let awaitingRemeasurement = 0;
  let coverageBlocked = 0;
  let newActivity = 0;

  for (const row of rows) {
    const stage = CONTROL_STAGES.includes(row.controlStage as ControlStage)
      ? (row.controlStage as ControlStage)
      : 'Confirmed';
    counts.set(stage, (counts.get(stage) || 0) + 1);
    if (AWAITING_IMPLEMENTATION.has(stage)) awaitingImplementation += 1;
    if (AWAITING_REMEASUREMENT.has(stage)) awaitingRemeasurement += 1;
    if (isCoverageBlocked(row)) coverageBlocked += 1;
    if (hasNewActivity(row)) newActivity += 1;
  }

  const byStage: ControlStageRollupBucket[] = CONTROL_STAGES
    .map((stage) => ({ stage, count: counts.get(stage) || 0 }))
    .filter((bucket) => bucket.count > 0);

  const operationalAll: ControlStageOperationalBucket[] = [
    {
      key: 'awaiting_implementation',
      label: 'Awaiting implementation',
      count: awaitingImplementation,
    },
    {
      key: 'awaiting_remeasurement',
      label: 'Awaiting remeasurement',
      count: awaitingRemeasurement,
    },
    {
      key: 'coverage_blocked',
      label: 'Coverage blocked',
      count: coverageBlocked,
    },
    {
      key: 'new_activity',
      label: 'New activity',
      count: newActivity,
    },
  ];
  const operational = operationalAll.filter((bucket) => bucket.count > 0);

  return {
    byStage,
    operational,
    total: rows.length,
  };
}

export function filterCasesByControlStage(
  rows: CaseRegisterRow[],
  stage: ControlStage | null,
): CaseRegisterRow[] {
  if (!stage) return rows;
  return rows.filter((row) => row.controlStage === stage);
}
